import { STATUS_CODE } from '@kc-console/core';
import { fetchEventSource } from '@microsoft/fetch-event-source';

import type { StreamEventPayload } from '../constants/types/stream';

interface StreamClientHandlers {
  onMessage?: (payload: StreamEventPayload) => void;
  onUnauthorized?: () => void;
}

interface StreamClientOptions {
  maxRetries?: number;
  onConnectionChange?: (connected: boolean) => void;
  onCountChange?: (count: number) => void;
}

class StreamClient {
  private abortController: AbortController | null = null;
  private isConnected = false;
  private handlers: StreamClientHandlers = {};
  private retryCount = 0;
  private connectedCount = 0;
  private terminalUnauthorized = false;
  private maxRetries: number;

  constructor(private options: StreamClientOptions) {
    this.maxRetries = options.maxRetries ?? 3;
  }

  updateHandlers(handlers: StreamClientHandlers) {
    this.handlers = handlers;
  }

  connect(token: string | undefined, streamUrl: string | undefined) {
    if (this.terminalUnauthorized) return;

    if (this.abortController) {
      if (this.isConnected) return;
      else this.disconnect();
    }

    if (!streamUrl) {
      console.error('[STREAM] streamUrl not available');
      return;
    }

    if (!token) {
      console.error('[STREAM] token not available');
      return;
    }

    const controller = new AbortController();
    this.abortController = controller;
    this.retryCount = 0;

    fetchEventSource(streamUrl, {
      method: 'GET',
      headers: {
        'x-auth-token': token,
        Accept: 'text/event-stream',
        Connection: 'keep-alive',
      },
      signal: controller.signal,
      onopen: async response => {
        if (response.ok && response.status === STATUS_CODE.OK) {
          this.retryCount = 0;
          this.setConnected(true);
          this.connectedCount += 1;
          this.options.onCountChange?.(this.connectedCount);
          return;
        }

        if (response.status === STATUS_CODE.UNAUTHORIZED) {
          if (this.terminalUnauthorized) return;

          this.terminalUnauthorized = true;
          this.disconnect();
          this.handlers.onUnauthorized?.();
          return;
        }

        if (
          response.status >= STATUS_CODE.BAD_REQUEST &&
          response.status < STATUS_CODE.INTERNAL_SERVER_ERROR &&
          response.status !== STATUS_CODE.TOO_MANY_REQUESTS
        ) {
          throw new Error(`SSE Connection Client Error: ${response.status}`);
        }

        if (
          response.status >= STATUS_CODE.INTERNAL_SERVER_ERROR ||
          response.status === STATUS_CODE.TOO_MANY_REQUESTS
        ) {
          throw new Error(`SSE Connection Server Error: ${response.status}`);
        }
      },
      onmessage: ({ data: sseData }) => {
        try {
          if (!sseData) return;
          const payload = JSON.parse(sseData) as StreamEventPayload;
          this.handlers.onMessage?.(payload);
        } catch (error) {
          console.error('[STREAM] Failed to parse message:', error);
        }
      },
      onerror: () => {
        this.setConnected(false);
        if (controller.signal.aborted) return;

        this.retryCount += 1;

        if (this.retryCount > this.maxRetries) {
          this.disconnect();
          throw new Error(`[STREAM] retry limit exceeded (${this.maxRetries})`);
        }
      },
      onclose: () => {
        this.setConnected(false);
        this.abortController = null;
      },
      openWhenHidden: true, // 백그라운드에서도 연결 유지
    })
      .catch(error => {
        if (controller.signal.aborted) return;
        console.error('[STREAM] connection terminated:', error);
      })
      .finally(() => {
        if (this.abortController === controller) {
          this.abortController = null;
        }
      });
  }

  disconnect() {
    if (!this.abortController) return;
    this.abortController.abort();
    this.abortController = null;
    this.setConnected(false);
  }

  get connected() {
    return this.isConnected;
  }

  get count() {
    return this.connectedCount;
  }

  private setConnected(connected: boolean) {
    if (this.isConnected === connected) return;
    this.isConnected = connected;
    this.options.onConnectionChange?.(connected);
  }
}

export default StreamClient;
